// ============================================================
// 📁 آدرس این فایل در پروژه:
//    src/pages/finance/journal/PendingControls.tsx
// 
// این فایل را در همان مسیر بالا کپی کنید (روی فایل قبلی بنویسید)
// ============================================================

import { Clock, CheckCircle, RotateCcw } from "lucide-react";

// =====================================================
// PendingControls.tsx
// کامپوننت‌های مشترک برای منطق "در انتظار / تأیید شده"
// در همه ۷ ماژول جورنال استفاده می‌شود
// =====================================================

const FONT = "'Vazirmatn','Segoe UI',sans-serif";

// رنگ‌های استاندارد
export const PENDING_COLOR = "#f59e0b";
export const CONFIRMED_COLOR = "#10b981";

// ─────────────────────────────────────────────────────
// 1. تب‌های فیلتر وضعیت (در بالای لیست)
// ─────────────────────────────────────────────────────
export function StatusTabs({
  value,
  onChange,
  totalCount,
  pendingCount,
  T,
  dark,
  pendingLabel = "در انتظار",
  confirmedLabel = "تأیید شده",
  pendingValue = "pending",
  confirmedValue = "confirmed",
}: {
  value: string;
  onChange: (v: any) => void;
  totalCount: number;
  pendingCount: number;
  T: any;
  dark: boolean;
  pendingLabel?: string;
  confirmedLabel?: string;
  pendingValue?: string;
  confirmedValue?: string;
}) {
  const confirmedCount = totalCount - pendingCount;
  const tabs = [
    { id: "all",            label: "همه",          count: totalCount,     color: "#64748b" },
    { id: pendingValue,     label: pendingLabel,   count: pendingCount,   color: PENDING_COLOR },
    { id: confirmedValue,   label: confirmedLabel, count: confirmedCount, color: CONFIRMED_COLOR },
  ];

  return (
    <div style={{
      display: "inline-flex", gap: 4, padding: 5, borderRadius: 12,
      background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)",
      border: `1px solid ${T.cardBorder}`,
    }}>
      {tabs.map(tab => {
        const active = value === tab.id;
        return (
          <button
            key={tab.id}
            onClick={() => onChange(tab.id)}
            style={{
              display: "flex", alignItems: "center", gap: 7,
              padding: "8px 16px", borderRadius: 9, border: "none",
              cursor: "pointer", fontSize: 12.5, fontWeight: 700,
              fontFamily: FONT,
              background: active
                ? `linear-gradient(135deg,${tab.color},${tab.color}d4)`
                : "transparent",
              color: active ? "#fff" : T.subtext,
              transition: "all 0.2s",
              boxShadow: active ? `0 2px 8px ${tab.color}55` : "none",
              whiteSpace: "nowrap",
            }}
          >
            <span>{tab.label}</span>
            <span style={{
              fontSize: 10.5, fontWeight: 800,
              padding: "2px 8px", borderRadius: 8,
              background: active ? "rgba(255,255,255,0.25)" : `${tab.color}25`,
              color: active ? "#fff" : tab.color,
              minWidth: 20, textAlign: "center",
            }}>
              {tab.count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────
// 2. بدج "در انتظار" که کنار شماره سند نمایش داده می‌شود
// ─────────────────────────────────────────────────────
export function PendingBadge() {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "3px 10px", borderRadius: 7,
      background: "linear-gradient(135deg,#fef3c7,#fde68a)",
      color: "#b45309",
      fontSize: 11, fontWeight: 800,
      fontFamily: FONT,
      border: "1px solid #fbbf2440",
      boxShadow: "0 1px 3px rgba(245,158,11,0.15)",
    }}>
      <Clock size={11} strokeWidth={2.5}/>
      در انتظار
    </span>
  );
}

// ─────────────────────────────────────────────────────
// 3. سبک ردیف pending (border-left نارنجی + پس‌زمینه)
// ─────────────────────────────────────────────────────
export function pendingRowStyle(dark: boolean): React.CSSProperties {
  return {
    borderLeft: `4px solid ${PENDING_COLOR}`,
    background: dark ? "rgba(245,158,11,0.06)" : "rgba(254,243,199,0.5)",
    boxShadow: "inset 0 0 0 1px rgba(245,158,11,0.08)",
  };
}

// ─────────────────────────────────────────────────────
// 4. دکمه آیکون "تأیید نهایی" (سبز با ✓)
// ─────────────────────────────────────────────────────
export function ConfirmIconButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      title="تأیید نهایی"
      style={{
        width: 30, height: 30, borderRadius: 8, border: "none",
        background: "linear-gradient(135deg,#10b981,#059669)",
        color: "#fff", cursor: "pointer",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 2px 6px rgba(16,185,129,0.35)",
        transition: "transform 0.15s",
      }}
      onMouseEnter={e => (e.currentTarget.style.transform = "scale(1.08)")}
      onMouseLeave={e => (e.currentTarget.style.transform = "scale(1)")}
    >
      <CheckCircle size={14} strokeWidth={2.5}/>
    </button>
  );
}

// ─────────────────────────────────────────────────────
// 5. دکمه آیکون "برگشت به انتظار" (نارنجی با ↻)
// ─────────────────────────────────────────────────────
export function RevertIconButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      title="برگشت به انتظار"
      style={{
        width: 30, height: 30, borderRadius: 8, border: "none",
        background: `${PENDING_COLOR}20`,
        color: PENDING_COLOR, cursor: "pointer",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        transition: "all 0.15s",
      }}
      onMouseEnter={e => {
        e.currentTarget.style.background = `${PENDING_COLOR}35`;
        e.currentTarget.style.transform = "scale(1.08)";
      }}
      onMouseLeave={e => {
        e.currentTarget.style.background = `${PENDING_COLOR}20`;
        e.currentTarget.style.transform = "scale(1)";
      }}
    >
      <RotateCcw size={13} strokeWidth={2.5}/>
    </button>
  );
}

// ─────────────────────────────────────────────────────
// 6. دکمه "در انتظار" در فرم ثبت
// ─────────────────────────────────────────────────────
export function PendingSubmitButton({
  onClick,
  loading,
  label = "ذخیره به‌عنوان در انتظار",
}: {
  onClick: () => void;
  loading: boolean;
  label?: string;
}) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      style={{
        display: "flex", alignItems: "center", gap: 7,
        padding: "11px 22px", borderRadius: 11,
        border: `1.5px solid ${loading ? "#94a3b8" : PENDING_COLOR}`,
        background: loading ? "#94a3b820" : `${PENDING_COLOR}12`,
        color: loading ? "#94a3b8" : PENDING_COLOR,
        fontSize: 13, fontWeight: 800, fontFamily: FONT,
        cursor: loading ? "not-allowed" : "pointer",
        transition: "all 0.2s",
      }}
      onMouseEnter={e => {
        if (!loading) {
          e.currentTarget.style.background = `${PENDING_COLOR}22`;
          e.currentTarget.style.transform = "translateY(-1px)";
          e.currentTarget.style.boxShadow = `0 4px 12px ${PENDING_COLOR}30`;
        }
      }}
      onMouseLeave={e => {
        if (!loading) {
          e.currentTarget.style.background = `${PENDING_COLOR}12`;
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "none";
        }
      }}
    >
      <Clock size={14} strokeWidth={2.5}/>
      {loading ? "..." : label}
    </button>
  );
}

// ─────────────────────────────────────────────────────
// 7. دکمه "ثبت نهایی" در فرم ثبت
// ─────────────────────────────────────────────────────
export function ConfirmSubmitButton({
  onClick,
  loading,
  color,
  label = "ثبت نهایی",
}: {
  onClick: () => void;
  loading: boolean;
  color: string;
  label?: string;
}) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      style={{
        display: "flex", alignItems: "center", gap: 7,
        padding: "11px 24px", borderRadius: 11, border: "none",
        background: loading ? "#94a3b8" : `linear-gradient(135deg,${color},${color}cc)`,
        color: "#fff", fontSize: 13, fontWeight: 800, fontFamily: FONT,
        cursor: loading ? "not-allowed" : "pointer",
        boxShadow: loading ? "none" : `0 4px 12px ${color}50`,
        transition: "all 0.2s",
      }}
      onMouseEnter={e => {
        if (!loading) {
          e.currentTarget.style.transform = "translateY(-1px)";
          e.currentTarget.style.boxShadow = `0 6px 16px ${color}60`;
        }
      }}
      onMouseLeave={e => {
        if (!loading) {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = `0 4px 12px ${color}50`;
        }
      }}
    >
      <CheckCircle size={14} strokeWidth={2.5}/>
      {loading ? "در حال ثبت..." : label}
    </button>
  );
}

// ─────────────────────────────────────────────────────
// 8. توضیح کوتاه زیر دکمه‌های فرم ثبت
// ─────────────────────────────────────────────────────
export function PendingHint({ T, customText }: { T: any; customText?: string }) {
  const text = customText ?? "از «در انتظار» برای ذخیره موقتی بدون تأثیر مالی استفاده کنید.";
  return (
    <div style={{
      fontSize: 11, color: T.subtext,
      display: "flex", alignItems: "center", gap: 6,
      lineHeight: 1.5,
    }}>
      <span style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: 18, height: 18, borderRadius: "50%",
        background: `${PENDING_COLOR}25`, color: PENDING_COLOR,
        fontSize: 11, fontWeight: 800, flexShrink: 0,
      }}>i</span>
      <span style={{ maxWidth: 360 }}>{text}</span>
    </div>
  );
}